<template>
    <main
        v-if="faqs"
        class="relative pb-10 pt-25 md:pb-15 md:pt-36">
        <TheBreadcrumbs
            v-if="isCromi()"
            :items="breadcrumbs"
            class="mb-5" />

        <LazyApiSection
            recovery-required
            :recovery-ready="!!categories?.length"
            min-height="32rem"
            @visible="loadDeferredFaqContent">
            <section
                v-if="faqs && categories"
                class="container mt-5 lg:flex">
                <template v-if="!isRadioguide()">
                    <nav class="relative z-30 w-full lg:max-w-[14rem]">
                        <ul class="sticky top-25 space-y-2 max-lg:hidden">
                            <li
                                v-for="category in categories"
                                :key="category.category_slug">
                                <button
                                    :class="
                                        category.category_slug ===
                                        activeCategorySlug
                                            ? 'bg-[#3A036B]'
                                            : 'bg-white/5'
                                    "
                                    class="w-full rounded-12 p-5 text-start text-16/132 transition-colors duration-300"
                                    :disabled="
                                        category.category_slug ===
                                        activeCategorySlug
                                    "
                                    @click="
                                        changeCategory(category.category_slug)
                                    ">
                                    {{ category.title }}
                                </button>
                            </li>
                        </ul>
                    </nav>
                </template>
                <div class="w-full max-lg:mt-5 lg:ml-5">
                    <AtomTitle
                        level="1"
                        class="md:max-w-[50%]">
                        {{ faqs.data.name }}
                    </AtomTitle>
                    <FaqCategory
                        :data="categories"
                        v-model="activeQuestionSlug" />
                </div>
            </section>
        </LazyApiSection>
        <AtomHr class="pt-10 md:pt-20 lg:pt-20" />

        <RemainingQuestionsSection class="py-10 md:py-20" />
    </main>
</template>

<script setup lang="ts">
import type { FAQPage } from '~/types/pages.types'

import AtomHr from '~/components/atom/atom-hr/AtomHr.vue'
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import TheBreadcrumbs, {
    type BreadcrumbItem,
} from '~/components/base/the-breadcrumbs/TheBreadcrumbs.vue'
import FaqCategory from '~/components/pages/faq/faq-category/FaqCategory.vue'
import type { FaqCategoryType } from '~/components/pages/faq/faq-category/faq-categories.types'
import LazyApiSection from '~/components/particles/lazy-api-section/LazyApiSection.vue'
import RemainingQuestionsSection from '~/components/particles/remaining-questions-section/RemainingQuestionsSection.vue'

const route = useRoute()
const router = useRouter()
const modalStore = useModalStore()
const nuxtApp = useNuxtApp()
const config = useRuntimeConfig()

const breadcrumbs: BreadcrumbItem[] = [
    {
        label: 'Главная',
        to: '/',
    },
    {
        label: 'Вопрос-ответ',
    },
]

const { data: faqs, error } = await useApiFetch<Page<FAQPage>>(`/v1/faqs`, {
    key: 'microapp-faq-page',
    getCachedData(key) {
        return nuxtApp.payload?.data?.[key]
    },
    useLastModified: true,
})

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

const { data: deferredFaqs, execute: executeDeferredFaqs } = await useApiFetch<
    Page<FAQPage>
>(`/v1/faqs`, {
    key: 'deferred-microapp-faq-page',
    query: {
        include: 'faqs',
    },
    server: false,
    immediate: false,
    watch: false,
    getCachedData(key) {
        return nuxtApp.payload?.data?.[key]
    },
})

const schemaFaqs = import.meta.server
    ? await $fetch<Page<FAQPage>>('/v1/faqs', {
          baseURL: config.public.api_url,
          headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Office-Cromi-Domain': config.public.website as string,
          },
          query: {
              include: 'faqs',
          },
      }).catch(() => null)
    : null

const isDeferredFaqContentLoading = ref(false)

async function loadDeferredFaqContent() {
    if (isDeferredFaqContentLoading.value || deferredFaqs.value) return

    isDeferredFaqContentLoading.value = true

    await executeDeferredFaqs()

    isDeferredFaqContentLoading.value = false
}

if (faqs.value?.data) {
    modalStore.setFeedbackLabel(faqs.value.data?.feedback_label || '')
} else {
    modalStore.setFeedbackLabel('')
}

const categories = computed<FaqCategoryType[] | null>(() => {
    if (!deferredFaqs.value) return null

    return deferredFaqs.value.data.faqs.data
})

const { activeId } = useScrollspy(
    () => categories.value?.map((category) => category.category_slug) || [],
    {
        rootMargin: '0px -20% -20% 0px',
    }
)

const initialCategoryState = getFallbackCategoryState()

const activeCategorySlug = ref<string | null>(initialCategoryState.categorySlug)
const activeQuestionSlug = ref<string | undefined>(
    initialCategoryState.questionSlug
)
const FAQ_ACCORDION_ANIMATION_MS = 450
const FAQ_SCROLL_DELAY_MS = FAQ_ACCORDION_ANIMATION_MS + 50
const HEADER_SCROLL_GAP_PX = 16

let categoryScrollTimeout: ReturnType<typeof setTimeout> | null = null

onMounted(() => {
    const categoryState = setCategory()

    if (categoryState.categorySlug && isCategoryHash(route.hash)) {
        scheduleCategoryScroll(categoryState.categorySlug)
    }
})

watch(
    categories,
    async () => {
        const categoryState = setCategory()

        if (categoryState.categorySlug && isCategoryHash(route.hash)) {
            await nextTick()
            scheduleCategoryScroll(categoryState.categorySlug)
        }
    },
    { flush: 'post' }
)

onBeforeUnmount(() => {
    if (categoryScrollTimeout) {
        clearTimeout(categoryScrollTimeout)
    }
})

watch(
    () => route.hash,
    (newHash, oldHash) => {
        if (newHash !== oldHash) {
            const categoryState = setCategory()

            if (categoryState.categorySlug && isCategoryHash(newHash)) {
                scheduleCategoryScroll(categoryState.categorySlug)
            }
        }
    }
)

watch(activeId, () => {
    activeCategorySlug.value = activeId.value
})

async function changeCategory(slug: string) {
    if (slug === activeCategorySlug.value) return

    activeCategorySlug.value = slug
    activeQuestionSlug.value = getFirstQuestionSlug(slug)
    await nextTick()

    await router.push(getAnchorLink('/faq/' + '#' + slug))
}

function setCategory() {
    const categoryState = getCategoryState()

    activeCategorySlug.value = categoryState.categorySlug
    activeQuestionSlug.value = categoryState.questionSlug

    return categoryState
}

function getFallbackCategoryState() {
    const fallbackCategory = categories.value?.[0] || null

    return {
        categorySlug: fallbackCategory?.category_slug || null,
        questionSlug: fallbackCategory?.data?.[0]?.slug || undefined,
    }
}

function getCategoryState() {
    const fallbackState = getFallbackCategoryState()
    const hashString = route.hash.replace('#', '')

    if (!hashString) return fallbackState

    const matchedCategory = categories.value?.find((category) => {
        if (category.category_slug === hashString) {
            return true
        }

        const isQuestionMatch = category.data?.some(
            (question) => question.slug === hashString
        )
        if (isQuestionMatch) return true

        return false
    })

    if (!matchedCategory) return fallbackState

    return {
        categorySlug: matchedCategory.category_slug,
        questionSlug:
            matchedCategory.category_slug === hashString
                ? matchedCategory.data?.[0]?.slug || fallbackState.questionSlug
                : hashString,
    }
}

function getFirstQuestionSlug(categorySlug: string): string | undefined {
    const category = categories.value?.find(
        (item) => item.category_slug === categorySlug
    )

    return category?.data?.[0]?.slug || undefined
}

function isCategoryHash(hash: string): boolean {
    const hashString = hash.replace('#', '')

    return Boolean(
        hashString &&
            categories.value?.some(
                (category) => category.category_slug === hashString
            )
    )
}

function scheduleCategoryScroll(categorySlug: string) {
    if (categoryScrollTimeout) {
        clearTimeout(categoryScrollTimeout)
    }

    categoryScrollTimeout = setTimeout(() => {
        scrollToCategory(categorySlug)
        categoryScrollTimeout = null
    }, FAQ_SCROLL_DELAY_MS)
}

function scrollToCategory(categorySlug: string) {
    const categoryElement = document.getElementById(categorySlug)

    if (!categoryElement) return

    const headerElement = document.querySelector('header')
    const headerOffset =
        (headerElement?.getBoundingClientRect().bottom || 0) +
        HEADER_SCROLL_GAP_PX
    const top =
        categoryElement.getBoundingClientRect().top +
        window.scrollY -
        headerOffset

    window.scrollTo({
        top: Math.max(top, 0),
        behavior: 'smooth',
    })
}

if (faqs.value) {
    useSeoMeta({
        title: faqs.value.data.seo.seo_title,
        description: faqs.value.data.seo.seo_description,
        ogTitle: faqs.value.data.seo.og_title,
        ogDescription: faqs.value.data.seo.og_description,
        ogImage: faqs.value.data.seo.og_image
            ? faqs.value.data.seo.og_image
            : null,
    })

    if (schemaFaqs?.data.faqs?.data) {
        useSchemaOrg([
            defineWebPage({
                '@type': 'FAQPage',
                inLanguage: 'ru',
                mainEntity: schemaFaqs.data.faqs.data
                    .map((category) => category.data)
                    .flat()
                    .map((item) =>
                        defineQuestion({
                            name: item.question,
                            acceptedAnswer: stripHtml(item.answer),
                            inLanguage: 'ru',
                        })
                    ),
            }),
        ])
    }
}
</script>
